import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useLike } from "../hooks/useLike";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

function getDailyIndex(total) {
  const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
  return day % total;
}

export default function TodaysHighlight() {
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const { liked, count: likeCount, toggleLike } = useLike(place?.id || null, place?.likes || 0);
  const { language, t } = useLanguage();

  useEffect(() => {
    const load = async () => {
      try {
        const snapshot = await getDocs(collection(db, "places"));
        const places = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.image);
        if (places.length === 0) { setLoading(false); return; }
        setPlace(places[getDailyIndex(places.length)]);
      } catch (err) {
        console.error("Error loading highlight:", err);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (!loading && !place) return null;

  return (
    <section style={{ background: "transparent" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "96px 64px", boxSizing: "border-box" }} className="highlight-section">
        {loading ? (
          <div style={{ borderRadius: 24, height: 520, background: "linear-gradient(90deg, #e6eff8 25%, #f0f6ff 50%, #e6eff8 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />
        ) : (
          <div style={{ position: "relative", borderRadius: 24, overflow: "hidden", height: "clamp(400px, 55vh, 560px)", boxShadow: "0 24px 64px rgba(0,0,0,0.2)" }}>
            <img
              src={optimizedImageUrl(place.image, 1400)}
              alt={place.name}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(5,8,15,0.92) 0%, rgba(5,8,15,0.4) 55%, rgba(5,8,15,0.15) 100%)" }} />

            {/* Top bar */}
            <div style={{ position: "absolute", top: 24, left: 24, right: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {/* Category pill */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(10,12,20,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "7px 16px" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 15, color: "#fff", fontVariationSettings: "'FILL' 1" }}>landscape</span>
                <span style={{ fontFamily: "Geist, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fff" }}>
                  {language === "ka" && place.categoryKa ? place.categoryKa : place.category}
                </span>
              </div>

              {/* Likes pill */}
              <button
                onClick={toggleLike}
                style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 999, padding: "7px 16px", cursor: "pointer", color: "#fff", fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600, transition: "background 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: liked ? "#ff6b6b" : "#fff", fontVariationSettings: liked ? "'FILL' 1" : "'FILL' 0", transition: "all 0.2s" }}>favorite</span>
                {likeCount.toLocaleString()}
              </button>
            </div>

            {/* Bottom content */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "40px 32px 32px" }}>
              <h2 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#fff", margin: "0 0 10px", lineHeight: 1.05 }}>
                {language === "ka" && place.nameKa ? place.nameKa : place.name}
              </h2>

              {/* Elevation + location row */}
              {(place.stats?.elevation || place.location) && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                  {place.stats?.elevation && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }}>landscape</span>
                      <span style={{ fontFamily: "Geist, sans-serif", fontSize: 14, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>{place.stats.elevation}</span>
                    </div>
                  )}
                  {place.stats?.elevation && place.location && (
                    <span style={{ color: "rgba(255,255,255,0.3)" }}>|</span>
                  )}
                  {place.location && (
                    <span style={{ fontFamily: "Geist, sans-serif", fontSize: 14, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>{place.location}</span>
                  )}
                </div>
              )}

              {/* CTA */}
              <Link
                to={sectionPath(language, "place", resolveSlug(place, language))}
                style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", color: "#0a0c12", padding: "13px 28px", borderRadius: 999, fontFamily: "Geist, sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "none", transition: "all 0.2s", boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.3)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.2)"; }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>location_on</span>
                {language === "ka" ? "იხილე ლოკაციები" : "View locations"}
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
              </Link>
            </div>
          </div>
        )}

        <style>{`
          @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
          @media (max-width: 1024px) { .highlight-section { padding: 60px 32px !important; } }
          @media (max-width: 768px) { .highlight-section { padding: 48px 20px !important; } }
        `}</style>
      </div>
    </section>
  );
}
